/**
 * 벌점 관리 메인 컴포넌트
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePenaltyManagement } from '../../use-cases/usePenaltyManagement';
import { PenaltySummaryTab } from './PenaltySummaryTab';
import { PenaltyRecordsTab } from './PenaltyRecordsTab';
import { PenaltySettingsTab } from './PenaltySettingsTab';
import { WeeklyTop3Tab } from './WeeklyTop3Tab';
import { StudentStatsTab } from './StudentStatsTab';
import { PenaltyReviewTab } from './PenaltyReviewTab';
import { PenaltyIssueModal } from './PenaltyIssueModal';
import { PenaltyRecordFilter } from '../../models/Penalty';

type TabType = 'summary' | 'review' | 'records' | 'weeklyTop3' | 'studentStats' | 'settings';

interface TabConfig {
  id: TabType;
  label: string;
}

const TABS: TabConfig[] = [
  { id: 'summary', label: '상벌점 현황' },
  { id: 'review', label: '✓ 벌점 검수' },
  { id: 'records', label: '상벌점 기록' },
  { id: 'weeklyTop3', label: '🏆 주간 순공 랭킹' },
  { id: 'studentStats', label: '📋 전체 학생 현황' },
  { id: 'settings', label: '설정' },
];

export const PenaltyManagement: React.FC = () => {
  // 2026-10-06 — 순찰 노트 화면의 [벌점 관리에서 변경] 버튼이 ?tab=settings#patrol-items 로 바로 연다
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return (TABS.some((x) => x.id === t) ? t : 'summary') as TabType;
  });
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false);

  // 기록 탭의 현재 필터 상태를 유지하기 위한 ref
  const recordsFilterRef = useRef<PenaltyRecordFilter>({ include_cancelled: true });

  const {
    types,
    thresholds,
    setting,
    records,
    summaries,
    statistics,
    isLoading,
    error,
    recordsTotal,
    recordsPage,
    summariesTotal,
    summariesPage,
    loadTypes,
    loadThresholds,
    loadSetting,
    loadRecords,
    loadSummaries,
    loadStatistics,
    createType,
    updateType,
    deleteType,
    initTypes,
    createThreshold,
    updateThreshold,
    deleteThreshold,
    updateSetting,
    createRecord,
    cancelRecord,
    reduceRecord,
    cancelReduction,
    restoreRecord,
    resetPenalties,
    clearError,
  } = usePenaltyManagement();

  // 초기 로드: types는 벌점 부과 모달에서도 필요하므로 항상 로드
  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    if (activeTab === 'summary') {
      loadSummaries();
      loadStatistics();
    } else if (activeTab === 'records') {
      loadRecords();
    } else if (activeTab === 'settings') {
      loadTypes(true);  // 설정 탭에서는 비활성 유형도 포함
      loadThresholds(true);
      loadSetting();
    }
  }, [activeTab, loadSummaries, loadStatistics, loadRecords, loadTypes, loadThresholds, loadSetting]);

  // 현황 탭 페이지 변경 핸들러
  const handleSummariesPageChange = useCallback((page: number) => {
    loadSummaries(undefined, page);
  }, [loadSummaries]);

  // 기록 탭 페이지 변경 핸들러
  const handleRecordsPageChange = useCallback((page: number) => {
    loadRecords(recordsFilterRef.current, page);
  }, [loadRecords]);

  // 기록 탭 필터 변경 + 새로고침 핸들러
  const handleRecordsRefresh = useCallback(async (filter?: PenaltyRecordFilter) => {
    if (filter) {
      recordsFilterRef.current = filter;
    }
    await loadRecords(filter || recordsFilterRef.current);
  }, [loadRecords]);

  // 벌점 부과 완료 핸들러
  const handleIssueComplete = async () => {
    setIsIssueModalOpen(false);
    await loadRecords();
    await loadSummaries();
    await loadStatistics();
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* 헤더 — 🔴 좁은 화면(휴대폰)에서는 제목과 버튼을 위아래로 놓는다.
          한 줄에 억지로 넣으면 버튼이 제목을 덮고 글자가 세로로 쪼개진다. */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">벌점 관리</h1>
            <p className="text-sm text-gray-500 mt-1">
              학생들의 벌점을 관리하고 현황을 확인합니다.
            </p>
          </div>
          <button
            onClick={() => setIsIssueModalOpen(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap flex-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            벌점 부과
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span>{error}</span>
          </div>
          <button onClick={clearError} className="text-red-700 hover:text-red-900">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* 탭 네비게이션 */}
      {/* 🔴 탭은 좁은 화면에서 **옆으로 밀어서** 본다.
          종전엔 6개를 억지로 나눠 담아 글자가 한 글자씩 세로로 쌓였다.
          whitespace-nowrap(줄바꿈 금지) + flex-none(찌그러짐 금지)이 핵심이다. */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="-mb-px flex gap-5 sm:gap-8 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-none
                ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-500">로딩 중...</span>
        </div>
      )}

      {/* 탭 컨텐츠 */}
      {!isLoading && (
        <>
          {activeTab === 'summary' && (
            <PenaltySummaryTab
              summaries={summaries}
              statistics={statistics}
              thresholds={thresholds}
              total={summariesTotal}
              currentPage={summariesPage}
              onPageChange={handleSummariesPageChange}
              onRefresh={() => {
                loadSummaries();
                loadStatistics();
              }}
            />
          )}

          {activeTab === 'records' && (
            <PenaltyRecordsTab
              records={records}
              types={types}
              total={recordsTotal}
              currentPage={recordsPage}
              onPageChange={handleRecordsPageChange}
              onCancelRecord={cancelRecord}
              onReduceRecord={reduceRecord}
              onCancelReduction={cancelReduction}
              onRestoreRecord={restoreRecord}
              onRefresh={handleRecordsRefresh}
              onIssue={() => setIsIssueModalOpen(true)}
            />
          )}

          {activeTab === 'review' && <PenaltyReviewTab />}

          {activeTab === 'weeklyTop3' && <WeeklyTop3Tab />}

          {activeTab === 'studentStats' && <StudentStatsTab />}

          {activeTab === 'settings' && (
            <PenaltySettingsTab
              types={types}
              thresholds={thresholds}
              setting={setting}
              onCreateType={createType}
              onUpdateType={updateType}
              onDeleteType={deleteType}
              onInitTypes={initTypes}
              onCreateThreshold={createThreshold}
              onUpdateThreshold={updateThreshold}
              onDeleteThreshold={deleteThreshold}
              onUpdateSetting={updateSetting}
              onResetPenalties={resetPenalties}
            />
          )}
        </>
      )}

      {/* 벌점 부과 모달 */}
      {isIssueModalOpen && (
        <PenaltyIssueModal
          types={types}
          onClose={() => setIsIssueModalOpen(false)}
          onSubmit={createRecord}
          onComplete={handleIssueComplete}
        />
      )}
    </div>
  );
};

export default PenaltyManagement;
